import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// DD-MM-YYYY for tables/reports where a compact numeric date is preferred
// (e.g. Chain Purchase Orders per user preference).
export const formatDateDMY = (dateString) => {
  if (!dateString) return '';
  // Accept both ISO (2026-07-04...) and yyyy-mm-dd — do NOT rely on Date
  // parsing for the yyyy-mm-dd short form because JS treats it as UTC and
  // can shift the day by one in IST. Split on 'T' first, then reformat.
  const s = String(dateString);
  const datePart = s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
  const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  // Fallback for non-ISO input
  const d = new Date(dateString);
  if (isNaN(d)) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const getDaysUntil = (dateString) => {
  if (!dateString) return 0;
  const target = new Date(dateString);
  const now = new Date();
  const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  return diff;
};

// Once an order has started dispatching (partially or fully) — or is closed —
// the delivery-date countdown should stop. Use this to decide whether to
// render the "Xd overdue" indicator on the order.
const TERMINAL_OR_DISPATCHED_STATUSES = new Set([
  'Dispatched',
  'Partially Dispatched',
  'Delivered',
  'Cancelled',
  'Completed',
]);
export const isOrderDispatchedOrClosed = (order) => {
  if (!order || !order.status) return false;
  return TERMINAL_OR_DISPATCHED_STATUSES.has(order.status);
};

// Days until delivery, but returns null when the order has already started
// dispatching (so the UI can skip the overdue counter entirely).
export const getActiveDaysUntil = (order) => {
  if (!order || !order.delivery_date) return null;
  if (isOrderDispatchedOrClosed(order)) return null;
  return getDaysUntil(order.delivery_date);
};

export const getStageColor = (stage) => {
  const colors = {
    "Order Received": "slate",
    "PO Raised to Factory": "blue",
    "CAD/CAM/Mould Prepared": "purple",
    "Raw Materials Issued": "indigo",
    "Casting": "orange",
    "On the Floor": "yellow",
    "Received from Factory": "lime",
    "Hallmark/Stamping": "emerald",
    "Certification": "teal",
    "Ready for Dispatch": "cyan",
    "Shipped": "green"
  };
  return colors[stage] || "slate";
};

export const getPriorityClass = (priority) => {
  switch (priority) {
    case "Urgent":
      return "priority-urgent";
    case "High":
      return "priority-high";
    default:
      return "priority-normal";
  }
};

export const getProgressPercentage = (ready, total) => {
  if (!total || total === 0) return 0;
  return Math.round((ready / total) * 100);
};
